import { useEffect, useState } from "react";
import BrandShell from "../components/BrandShell";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Download, ShieldCheck, Search, Wallet as WalletIcon, Send, Landmark, Globe2, Check, X, Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import { useNavigate } from "react-router-dom";

const fmt = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHOD_ICON = { uk_bank: Landmark, iban: Globe2, paypal: Send };

function WithdrawalQueue() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/withdrawals");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, verb, body = {}) => {
    setBusyId(id);
    try {
      await api.post(`/admin/withdrawals/${id}/${verb}`, body);
      toast.success(verb === "mark-paid" ? "Marked as paid — creator notified" : verb === "reject" ? "Rejected — balance returned to creator" : "Marked processing");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const markPaid = (w) => {
    const ref = window.prompt(`Bank / PayPal transaction reference for £${w.amount.toFixed(2)} to ${w.creator_name || w.creator_email}?`);
    if (!ref?.trim()) return;
    act(w.id, "mark-paid", { reference: ref.trim() });
  };

  const reject = (w) => {
    const reason = window.prompt(`Reject reason (creator will see this)?`);
    if (!reason?.trim()) return;
    act(w.id, "reject", { reject_reason: reason.trim() });
  };

  if (!data) return <div className="glass rounded-2xl p-8 animate-pulse text-zinc-500 text-sm">Loading withdrawal queue…</div>;

  const rows = data.rows || [];
  const totals = data.totals || {};

  return (
    <div className="space-y-4" data-testid="admin-withdrawal-queue">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
            <WalletIcon className="h-3.5 w-3.5 text-nexus-blue" /> Withdrawal queue
          </div>
          <h2 className="font-display font-bold text-2xl tracking-tighter">Creator payouts to send</h2>
          <p className="text-zinc-400 text-xs mt-1">Queued in order. Send via your bank / PayPal, then mark paid with the reference.</p>
        </div>
        <div className="flex gap-2 text-[10px] uppercase tracking-widest">
          <span className="px-2 py-1 rounded-full bg-nexus-blue/10 border border-nexus-blue/30 text-nexus-blue">Requested {fmt(totals.requested)}</span>
          <span className="px-2 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-300">Processing {fmt(totals.processing)}</span>
          <span className="px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">Paid {fmt(totals.paid)}</span>
        </div>
      </div>
      <div className="glass rounded-2xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-xs">No withdrawal requests yet. Creators will queue here when they hit the £25 minimum.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {rows.map((w) => {
              const MethodIcon = METHOD_ICON[w.method_type] || Landmark;
              const isExpanded = expandedId === w.id;
              const meth = w.method_snapshot || {};
              return (
                <div key={w.id} data-testid={`withdrawal-${w.id}`}>
                  <div className="p-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors">
                    <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                      <MethodIcon className="h-5 w-5 text-zinc-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate">{w.creator_name || w.creator_email}</span>
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-zinc-400 bg-white/[0.02]">
                          {w.method_type?.replace("_", " ") || "unknown"}
                        </span>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                          w.status === "paid" ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" :
                          w.status === "processing" ? "text-yellow-300 bg-yellow-500/10 border-yellow-500/30" :
                          w.status === "rejected" ? "text-nexus-pink bg-nexus-pink/10 border-nexus-pink/30" :
                          "text-nexus-blue bg-nexus-blue/10 border-nexus-blue/30"
                        }`}>
                          {w.status}
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-1 flex flex-wrap gap-3">
                        <span>{new Date(w.created_at).toLocaleString("en-GB")}</span>
                        <span>· {w.hire_ids?.length || 0} released hire(s)</span>
                        {w.reference && <span>· ref: {w.reference}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-bold text-lg tabular-nums">{fmt(w.amount)}</div>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : w.id)}
                        className="text-[11px] text-nexus-blue hover:underline mt-0.5"
                        data-testid={`withdrawal-toggle-${w.id}`}
                      >
                        {isExpanded ? "Hide details" : "Show payout details"}
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-white/[0.01] border-t border-white/5" data-testid={`withdrawal-details-${w.id}`}>
                      <div className="mt-3 rounded-xl bg-black/40 border border-white/5 p-4 font-mono text-xs text-zinc-300 space-y-1">
                        <div><span className="text-zinc-500">Account holder:</span> {meth.account_holder || "—"}</div>
                        {meth.type === "uk_bank" && (
                          <>
                            <div><span className="text-zinc-500">Sort code:</span> {meth.sort_code}</div>
                            <div><span className="text-zinc-500">Account number:</span> {meth.account_number}</div>
                          </>
                        )}
                        {meth.type === "iban" && (
                          <>
                            <div><span className="text-zinc-500">IBAN:</span> {meth.iban}</div>
                            {meth.swift_bic && <div><span className="text-zinc-500">SWIFT/BIC:</span> {meth.swift_bic}</div>}
                          </>
                        )}
                        {meth.type === "paypal" && (
                          <div><span className="text-zinc-500">PayPal email:</span> {meth.paypal_email}</div>
                        )}
                        <div><span className="text-zinc-500">Country:</span> {meth.country || "GB"}</div>
                        <div><span className="text-zinc-500">Creator email:</span> {w.creator_email}</div>
                        {w.reject_reason && <div className="text-nexus-pink"><span className="text-zinc-500">Rejected:</span> {w.reject_reason}</div>}
                      </div>
                      {(w.status === "requested" || w.status === "processing") && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {w.status === "requested" && (
                            <Button
                              onClick={() => act(w.id, "mark-processing")}
                              disabled={busyId === w.id}
                              variant="outline"
                              className="bg-white/5 border-white/15 text-white hover:bg-white/10 rounded-full"
                              data-testid={`withdrawal-processing-${w.id}`}
                            >
                              {busyId === w.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                              Mark processing
                            </Button>
                          )}
                          <Button
                            onClick={() => markPaid(w)}
                            disabled={busyId === w.id}
                            className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 rounded-full"
                            data-testid={`withdrawal-paid-${w.id}`}
                          >
                            <Check className="h-4 w-4 mr-2" /> Mark paid
                          </Button>
                          <Button
                            onClick={() => reject(w)}
                            disabled={busyId === w.id}
                            className="bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 rounded-full"
                            data-testid={`withdrawal-reject-${w.id}`}
                          >
                            <X className="h-4 w-4 mr-2" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPayouts() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/payouts");
      setData(r.data);
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) { toast.error("Admin only"); nav("/"); return; }
      if (code === 401) { toast.error("Log in first"); nav("/auth?mode=login"); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (hire_id, body) => {
    setSavingId(hire_id);
    try {
      await api.patch(`/admin/payouts/${hire_id}`, body);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const download = async () => {
    try {
      const r = await api.get("/admin/payouts.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nexus-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (!data) return <BrandShell title="Admin · Payouts"><div className="p-10 text-zinc-500 animate-pulse">Loading…</div></BrandShell>;

  const t = data.totals || {};
  const rows = (data.rows || []).filter((r) => {
    if (!filter.trim()) return true;
    const s = filter.toLowerCase();
    return [r.brand_name, r.creator_name, r.brand_email, r.creator_email, r.package_title, r.hire_id, r.status]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <BrandShell title="Admin · Payouts">
      <SEO title="Admin · Payouts · Nexus" description="Reconcile brand and creator payouts." path="/admin/payouts" />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-nexus-pink" /> Admin only
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">Payouts ledger</h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Track brand-received and creator-paid for every hire. Tick as you reconcile in the bank.</p>
          </div>
          <Button onClick={download} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="admin-download-csv">
            <Download className="h-4 w-4 mr-2" /> Download CSV
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "Hires total", v: t.count },
            { l: "Brand owed", v: fmt(t.brand_owed) },
            { l: "Creator outstanding", v: fmt(t.creator_outstanding), tone: "warn" },
            { l: "Creator paid", v: fmt(t.creator_paid), tone: "ok" },
          ].map((k, i) => (
            <div key={i} className="glass rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.l}</div>
              <div className={`font-display font-bold text-2xl mt-1 tabular-nums ${k.tone === "warn" ? "text-yellow-300" : k.tone === "ok" ? "text-emerald-300" : "text-white"}`}>{k.v}</div>
            </div>
          ))}
        </div>

        <WithdrawalQueue />

        <div className="relative max-w-md">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by brand, creator, hire id…" className="pl-10 bg-white/[0.02] border-white/10 text-white" data-testid="admin-filter" />
        </div>

        <div className="glass rounded-2xl p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <tr>
                  <th className="text-left p-3">Created</th>
                  <th className="text-left p-3">Brand</th>
                  <th className="text-left p-3">Creator</th>
                  <th className="text-left p-3">Package</th>
                  <th className="text-right p-3">Brand £</th>
                  <th className="text-right p-3">Creator £</th>
                  <th className="text-center p-3">Brand paid?</th>
                  <th className="text-center p-3">Creator paid?</th>
                  <th className="text-left p-3">Note</th>
                </tr>
              </thead>
              <tbody data-testid="admin-payouts-table">
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="p-8 text-center text-zinc-500 text-xs">No hires yet.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.hire_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${r.hire_id}`}>
                    <td className="p-3 text-[10px] uppercase tracking-widest text-zinc-500">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="p-3">
                      <div className="font-semibold truncate max-w-[160px]">{r.brand_name}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[160px]">{r.brand_email}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-semibold truncate max-w-[160px]">{r.creator_name}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[160px]">{r.creator_email}</div>
                    </td>
                    <td className="p-3 truncate max-w-[180px]">{r.package_title || "—"}</td>
                    <td className="p-3 text-right tabular-nums">{fmt(r.brand_total)}</td>
                    <td className="p-3 text-right tabular-nums text-nexus-blue">{fmt(r.creator_payout)}</td>
                    <td className="p-3 text-center">
                      <input type="checkbox" checked={!!r.brand_paid} disabled={savingId === r.hire_id}
                             onChange={(e) => patch(r.hire_id, { brand_paid: e.target.checked })}
                             className="h-4 w-4 accent-emerald-500 cursor-pointer"
                             data-testid={`admin-brand-paid-${r.hire_id}`} />
                    </td>
                    <td className="p-3 text-center">
                      <input type="checkbox" checked={!!r.creator_paid} disabled={savingId === r.hire_id}
                             onChange={(e) => patch(r.hire_id, { creator_paid: e.target.checked })}
                             className="h-4 w-4 accent-emerald-500 cursor-pointer"
                             data-testid={`admin-creator-paid-${r.hire_id}`} />
                    </td>
                    <td className="p-3">
                      <input type="text" defaultValue={r.admin_note || ""} placeholder="Add a note…"
                             onBlur={(e) => { if (e.target.value !== (r.admin_note || "")) patch(r.hire_id, { admin_note: e.target.value }); }}
                             className="bg-white/[0.02] border border-white/10 rounded-md text-white text-xs px-2 py-1 w-40"
                             data-testid={`admin-note-${r.hire_id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-zinc-500">Generated at {new Date(data.generated_at).toLocaleString("en-GB")}. Only visible to accounts in ADMIN_EMAILS.</p>
      </div>
    </BrandShell>
  );
}
